import * as CheckboxPrimitive from "@radix-ui/react-checkbox";
import * as React from "react";

import { IconTick } from "@/newComponents/icon/Icon";
import { cn } from "@/newComponents/utils/utils";
import { useUIStateStore } from "@/store/uiStateStore";

export type CheckboxProps = {
  label?: string;
  column?: boolean;
  smallFont?: boolean;
  isList?: boolean;
  options?: { label: string; value: string }[];
  showSelectAll?: boolean;
  onSelectionChange?: (selected: string[]) => void;
  selectedValues?: string[];
  tableHeader?: boolean;
  // Standard checkbox props
  checked?: boolean;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onCheckedChange?: (checked: boolean) => void;
  defaultChecked?: boolean;
  disabled?: boolean;
  id?: string;
  className?: string;
};

const defaultOptions: { label: string; value: string }[] = [];
const defaultSelectedValues: string[] = [];

const FormCheckbox = React.forwardRef<
  React.ElementRef<typeof CheckboxPrimitive.Root>,
  CheckboxProps
>(
  (
    {
      className,
      smallFont = false,
      column = false,
      label = "",
      id,
      checked,
      onCheckedChange,
      onChange,
      defaultChecked,
      disabled = false,
      isList = false,
      options = defaultOptions,
      showSelectAll = true,
      onSelectionChange,
      selectedValues = defaultSelectedValues,
      tableHeader = false,
      ...props
    },
    ref,
  ) => {
    const isTunecore = useUIStateStore(state => state.isTunecore);

    const checkboxId = id ?? `formCheckbox-${Math.random().toString(36).substring(2, 15)}`;

    // Common checkbox styles
    const baseCheckboxStyles = "h-5 w-5 shrink-0 rounded-sm border shadow-xs transition-shadow outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed";

    const getCheckboxStyles = (isTableHeader = false) => {
      if (isTableHeader) {
        return isTunecore
          ? "border-white bg-tunecore-light-grey focus-visible:ring-tunecore-green/50 data-[state=checked]:bg-tunecore-green data-[state=checked]:border-tunecore-green"
          : "border-sentric-mid-grey bg-white focus-visible:ring-sentric-orange/50 data-[state=checked]:bg-sentric-orange data-[state=checked]:border-sentric-orange";
      }
      return isTunecore
        ? "border-white focus-visible:ring-tunecore-green/50 data-[state=checked]:bg-tunecore-green data-[state=checked]:border-tunecore-green"
        : "border-sentric-dark-background bg-transparent focus-visible:ring-sentric-orange/50 data-[state=checked]:bg-sentric-orange data-[state=checked]:border-sentric-orange";
    };

    const selectAll = React.useMemo(
      () => selectedValues.length === options.length && options.length > 0,
      [selectedValues, options.length],
    );

    const toggleSelection = (value: string) => {
      if (onSelectionChange) {
        const newSelectedItems = selectedValues.includes(value)
          ? selectedValues.filter(item => item !== value)
          : [...selectedValues, value];
        onSelectionChange(newSelectedItems);
      }
    };

    const handleSelectAll = () => {
      if (onSelectionChange) {
        if (selectAll) {
          onSelectionChange([]);
        }
        else {
          onSelectionChange(options.map(option => option.value));
        }
      }
    };

    // Handle both old onChange and new onCheckedChange
    const handleCheckedChange = (checkedState: boolean) => {
      if (onCheckedChange) {
        onCheckedChange(checkedState);
      }
      if (onChange) {
        // Create a synthetic event for backwards compatibility
        const syntheticEvent = {
          target: { checked: checkedState },
          currentTarget: { checked: checkedState },
        } as React.ChangeEvent<HTMLInputElement>;
        onChange(syntheticEvent);
      }
    };

    if (isList) {
      return (
        <div className="flex flex-col items-start gap-3">
          {showSelectAll && options.length > 0 && ( // Only show select all if there are options
            <label
              className={`flex items-center gap-2 cursor-pointer ${isTunecore ? "text-tunecore-green" : "text-sentric-orange"}`}
              // Use onClick here for the label to toggle, but keep onChange on input for accessibility
              onClick={handleSelectAll}
            >
              <CheckboxPrimitive.Root
                checked={selectAll}
                onCheckedChange={(checkedState) => {
                  if (onSelectionChange) {
                    if (checkedState === true) {
                      onSelectionChange(options.map(option => option.value));
                    }
                    else {
                      onSelectionChange([]);
                    }
                  }
                }}
                className={cn(
                  baseCheckboxStyles,
                  getCheckboxStyles(),
                )}
              >
                <CheckboxPrimitive.Indicator
                  className="flex items-center justify-center text-current transition-none"
                >
                  <IconTick className={cn(
                    "stroke-[3]",
                    isTunecore ? "text-black" : "text-white",
                  )}
                  />
                </CheckboxPrimitive.Indicator>
              </CheckboxPrimitive.Root>
              <span className={`${smallFont ? "text-sm" : "text-md"} ${isTunecore ? "font-tunecore text-tunecore-green" : "font-sentric-alt text-sentric-orange"}`}>
                Select all
              </span>
            </label>
          )}

          {options.map(option => (
            <label
              key={option.value}
              className={`flex items-center gap-2 cursor-pointer ${isTunecore ? "text-white" : "text-black"}`}
            >
              <CheckboxPrimitive.Root
                checked={selectedValues.includes(option.value)}
                onCheckedChange={() => toggleSelection(option.value)}
                className={cn(
                  baseCheckboxStyles,
                  getCheckboxStyles(),
                )}
              >
                <CheckboxPrimitive.Indicator
                  className="flex items-center justify-center text-current transition-none"
                >
                  <IconTick className={cn(
                    "stroke-[3]",
                    isTunecore ? "text-black" : "text-white",
                  )}
                  />
                </CheckboxPrimitive.Indicator>
              </CheckboxPrimitive.Root>
              <span className={`${smallFont ? "text-sm" : "text-md"} ${isTunecore ? "font-tunecore" : "font-sentric-alt"}`}>{option.label}</span>
            </label>
          ))}
        </div>
      );
    }

    return (
      <div
        className={column ? "flex flex-col-reverse justify-evenly" : "flex items-center gap-2 "}
      >
        <CheckboxPrimitive.Root
          id={checkboxId}
          ref={ref}
          checked={checked}
          defaultChecked={defaultChecked}
          disabled={disabled}
          onCheckedChange={handleCheckedChange}
          className={cn(
            baseCheckboxStyles,
            getCheckboxStyles(tableHeader),
          )}
          {...props}
        >
          <CheckboxPrimitive.Indicator
            className="flex items-center justify-center transition-none"
          >
            <IconTick
              className={cn(
                "stroke-[3]",
                isTunecore ? "stroke-black" : "stroke-white",
              )}
            />
          </CheckboxPrimitive.Indicator>
        </CheckboxPrimitive.Root>
        <label
          className={`${
            smallFont ? "text-sm" : "text-md"
          } whitespace-wrap ${
            tableHeader
              ? "text-sentric-light-grey"
              : isTunecore
                ? "text-white"
                : "text-black"
          } ${
            isTunecore ? "font-tunecore" : "font-sentric-alt"
          } ${disabled ? "opacity-40 cursor-not-allowed" : "hover:cursor-pointer active:cursor-pointer"
          }`}
          htmlFor={checkboxId}
        >
          {label}
        </label>
      </div>
    );
  },
);

FormCheckbox.displayName = "FormCheckbox";

export { FormCheckbox };
